import { computedAsync } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import PersonalRest from '@exchange/libs/rest-api/personal-api';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import type { FuturesPublicTradingFlag } from '@exchange/libs/utils/launchdarkly/src/lib/defaults';

type FuturesFeatureFlagServiceState = {
  countryOfResidency: string | null | undefined;
};

const getDefaultState = (): FuturesFeatureFlagServiceState => ({
  countryOfResidency: null,
});

class FuturesFeatureFlagService {
  private state = reactive<FuturesFeatureFlagServiceState>(getDefaultState());

  private isFetchingCountryOfResidency = ref(false);

  private isFetchedCountryOfResidency = computed(() => this.state.countryOfResidency !== getDefaultState().countryOfResidency);

  private getCountryOfResidency = async () => {
    if (this.isFetchingCountryOfResidency.value) {
      return getDefaultState().countryOfResidency;
    }

    this.isFetchingCountryOfResidency.value = true;

    try {
      if (this.state.countryOfResidency) {
        return this.state.countryOfResidency;
      }

      const identity = await PersonalRest.Identity.get();

      this.state.countryOfResidency = identity.addressOfResidency.country;

      return this.state.countryOfResidency;
    } catch (error) {
      if ((error as unknown as { status: number }).status === 404) {
        return undefined;
      }

      return getDefaultState().countryOfResidency;
    } finally {
      this.isFetchingCountryOfResidency.value = false;
    }
  };

  private countryOfResidency = computedAsync(
    async () => {
      if (!authService.hasBeenAuthenticated) {
        return getDefaultState().countryOfResidency;
      }

      const countryOfResidency = await this.getCountryOfResidency();

      return countryOfResidency;
    },
    getDefaultState().countryOfResidency,
    { lazy: true },
  );

  public isEnabled = computed(() => launchdarkly.flags['futures-enabled'].value);

  public isReady = computed(() => launchdarkly.isAuthorizedFlagsReady.value && this.isFetchedCountryOfResidency.value);

  private publicTradingFlag = computed(() => launchdarkly.flags['futures-public-trading'].value as FuturesPublicTradingFlag);

  public isPublicTradingEnabled = computed(() => {
    // If the public trading flag is disabled or we don't have the user or the user is retail and we don't have a country of residency, then public trading is disabled
    if (!this.publicTradingFlag.value.enabled || !accountService.accountUser.value || (accountService.accountUser.value.isRetail && !this.countryOfResidency.value)) {
      return false;
    }

    // If the user is NOT retail or the public trading flag is enabled for all countries, then public trading is enabled
    if (!accountService.accountUser.value.isRetail || !this.publicTradingFlag.value.enabledForCountries) {
      return true;
    }

    // Otherwise the user is retail and the public trading flag is enabled for specific countries, so check if the country of residency is in the list of enabled countries
    return this.publicTradingFlag.value.enabledForCountries.includes(this.countryOfResidency.value as (typeof this.publicTradingFlag.value.enabledForCountries)[number]);
  });

  public isEnabledWithPublicTradingEnabled = computed(() => this.isEnabled.value && this.isPublicTradingEnabled.value);

  public reset = () => {
    Object.assign(this.state, getDefaultState());
  };
}

const futuresFeatureFlagService = new FuturesFeatureFlagService();

export default futuresFeatureFlagService;
